// Job 53: a bill validator needs to switch a charge (or a whole section) out of
// the *expected* invoice while validating, and see plainly that they did.
//
// The exclusions themselves live on bv_bill.calc_exclusions (migration 093), not
// in shadow_result, so a recalculation cannot wipe them. This file is the pure
// half: applyExclusions takes a calculated result plus the exclusion list and
// returns a result whose excluded lines are flagged and whose totals omit them —
// the lines are never deleted, so the omission is always explainable.

import type {
  ActualBillResult,
  BvBill,
  CalcExclusion,
  ShadowBillLine,
  ShadowBillResult,
  ShadowBillSection,
} from '../types/billTypes';
import { canonicalChargeName } from './chargeNames';
import { showsEnvironmental } from './environmental';

const GST_RATE = 0.1;
const round2 = (n: number) => parseFloat(n.toFixed(2));

/** True when this line is switched out by the exclusion list. A section-wide
 *  exclusion (`description: null`) drops every line in that section; otherwise
 *  the charge name must match.
 *
 *  The name is compared canonically, the same way the Comparison table's
 *  `isRowExcluded` compares it. An exclusion is written from a ChargeRow's
 *  `label` — already run through `canonicalChargeName` — so comparing it raw
 *  against a line description silently missed any charge whose stored wording
 *  differs from its canonical name, on either side. */
const nameKey = (description: string) => canonicalChargeName(description).trim().toLowerCase();

/** True when the whole section is switched out. GST has no lines of its own, so
 *  this is the only way to ask about it. */
export const isSectionExcluded = (
  section: ShadowBillSection,
  exclusions: CalcExclusion[] | null | undefined,
): boolean =>
  !!exclusions?.some((e) => e.section === section && e.description === null);

export const isLineExcluded = (
  line: { section: ShadowBillSection; description: string },
  exclusions: CalcExclusion[],
): boolean =>
  exclusions.some(
    (e) =>
      e.section === line.section &&
      (e.description === null || nameKey(e.description) === nameKey(line.description)),
  );

/**
 * Recompute a calculated result with the excluded charges left out of the
 * totals. Excluded lines are flagged (`excluded: true`) but kept, so a total can
 * always be explained by what it dropped.
 *
 * GST is recomputed as 10% of the *remaining* ex-GST subtotal — an exclusion must
 * not leave GST covering charges that are no longer counted. This is a calculated
 * expected invoice, so the 10% rule is exactly right here.
 *
 * An empty exclusion list returns the result untouched (referentially identical),
 * so a bill with nothing excluded is never re-derived.
 */
export const applyExclusions = (
  result: ShadowBillResult,
  exclusions: CalcExclusion[] | null | undefined,
): ShadowBillResult => {
  if (!exclusions || exclusions.length === 0) return result;

  const lines: ShadowBillLine[] = result.lines.map((l) => ({
    ...l,
    excluded: isLineExcluded(l, exclusions),
  }));

  const section_totals: Record<ShadowBillSection, number> = {
    retail: 0, distribution: 0, metering: 0, environmental: 0, other: 0, gst: 0,
  };
  for (const line of lines) {
    if (line.excluded) continue;
    if (line.section in section_totals) {
      section_totals[line.section] = round2(
        (section_totals[line.section] ?? 0) + (Number(line.amount) || 0),
      );
    }
  }

  const subtotal = round2(
    section_totals.retail + section_totals.distribution +
    section_totals.metering + section_totals.environmental + section_totals.other,
  );
  // GST is a section like any other and can be switched off. Excluded, it is
  // zero rather than 10% of what remains — the point of switching it out is to
  // compare the two invoices ex-GST on both sides.
  const gst = isSectionExcluded('gst', exclusions) ? 0 : round2(subtotal * GST_RATE);
  section_totals.gst = gst;
  const total = round2(subtotal + gst);

  return { ...result, lines, section_totals, subtotal, gst, total };
};

/** The excluded lines and their combined value — for the "N charges excluded"
 *  summary shown on the expected invoice and in the report. */
export const excludedSummary = (
  result: ShadowBillResult,
  exclusions: CalcExclusion[] | null | undefined,
): { count: number; total: number; lines: ShadowBillLine[] } => {
  if (!exclusions || exclusions.length === 0) return { count: 0, total: 0, lines: [] };
  const lines = result.lines.filter((l) => isLineExcluded(l, exclusions));
  const total = round2(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  return { count: lines.length, total, lines };
};

/**
 * The retailer's invoice with the same charges switched out.
 *
 * An exclusion has to leave BOTH sides, or the variance reports the removed
 * charge as a discrepancy — `comparisonTotals` in `comparisonRows.ts` states the
 * same rule, and this is that rule applied to the stored result the report reads.
 *
 * GST is passed through exactly as the retailer printed it. We never recompute
 * someone else's invoice — the 10% recomputation in `applyExclusions` above is
 * only valid because that side is our own calculation.
 */
export const applyExclusionsToActual = (
  result: ActualBillResult,
  exclusions: CalcExclusion[] | null | undefined,
): ActualBillResult => {
  if (!exclusions || exclusions.length === 0) return result;

  const lines = result.lines.map((l) => ({ ...l, excluded: isLineExcluded(l, exclusions) }));

  const gst = isSectionExcluded('gst', exclusions) ? 0 : result.gst;

  const section_totals: Record<ShadowBillSection, number> = {
    retail: 0, distribution: 0, metering: 0, environmental: 0, other: 0, gst,
  };
  for (const line of lines) {
    if (line.excluded || line.section === 'gst') continue;
    if (line.section in section_totals) {
      section_totals[line.section] = round2(
        (section_totals[line.section] ?? 0) + (Number(line.amount) || 0),
      );
    }
  }

  const subtotal = round2(
    section_totals.retail + section_totals.distribution +
    section_totals.metering + section_totals.environmental + section_totals.other,
  );

  return { ...result, lines, section_totals, subtotal, gst, total: round2(subtotal + gst) };
};

/**
 * The exclusions that actually apply to this bill: the ones stored on the row,
 * plus the site-level rule that a site without GreenPower is not compared on
 * environmental charges (job 21).
 *
 * That rule used to only hide the row. The amounts stayed in `subtotal` and
 * `total`, so the report printed a table whose visible rows did not add up to
 * the total beneath them — the difference being exactly the environmental
 * charges nobody could see. A section dropped from the comparison has to leave
 * the totals too, and expressing it as an exclusion means every reader that
 * already honours exclusions honours this as well.
 *
 * NEVER persist the result: the environmental entry is derived from the site's
 * GreenPower flag on each read, and writing it back would survive that flag
 * being turned on. Store what the user toggled; compute this at the point of use.
 */
export const effectiveExclusions = (
  bill: { site?: BvBill['site']; calc_exclusions?: CalcExclusion[] | null },
  stored?: CalcExclusion[] | null,
): CalcExclusion[] => {
  const list = stored ?? (Array.isArray(bill.calc_exclusions) ? bill.calc_exclusions : []);
  if (showsEnvironmental(bill.site)) return list;
  if (isSectionExcluded('environmental', list)) return list;
  return [...list, { section: 'environmental', description: null }];
};
